import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Newspaper, Star, BookOpen, BarChart2, UserCheck, Gavel, CheckCircle } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { buildBreadcrumbs } from "@/lib/structured-data";
import { Link } from "react-router-dom";
import { newsletterSchema, type NewsletterFormData } from "@/lib/validation";
import { sanitizeEmail } from "@/lib/sanitize";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";

export default function NewsletterPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const form = useForm<NewsletterFormData>({
    resolver: zodResolver(newsletterSchema),
    defaultValues: {
      email: "",
    },
    mode: "onBlur",
  });

  // Scroll animation effect
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate');
        }
      });
    }, observerOptions);

    const animateElements = document.querySelectorAll('.scroll-animate');
    animateElements.forEach((el) => observer.observe(el));

    return () => {
      animateElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  const onSubmit = async (data: NewsletterFormData) => {
    setIsSubmitting(true);

    try {
      // Sanitize email before sending
      const sanitizedEmail = sanitizeEmail(data.email);

      const response = await fetch('/api/newsletter-signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: sanitizedEmail }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setIsSubmitted(true);
        form.reset();

        // Reset success message after 10 seconds
        setTimeout(() => {
          setIsSubmitted(false);
        }, 10000);
      } else {
        // Handle server-side validation errors
        if (result.fieldErrors?.email) {
          form.setError("email", {
            type: "server",
            message: result.fieldErrors.email,
          });
        } else {
          form.setError("email", {
            type: "server",
            message: result.error || 'Failed to subscribe. Please try again.',
          });
        }
      }
    } catch (error) {
      console.error('Newsletter signup error:', error);
      form.setError("email", {
        type: "server",
        message: 'An unexpected error occurred. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const features = [
    {
      icon: Newspaper,
      title: "Industry Insights – Trends You Can't Afford to Miss",
      description: "Stay ahead of the curve with insider knowledge on the latest trends, breakthrough innovations, and industry shifts."
    },
    {
      icon: Star,
      title: "Expert Spotlight – Unfiltered Wisdom from the Brightest Minds",
      description: "Exclusive interviews and insights from industry leaders, disruptors, and visionaries who are rewriting the rules."
    },
    {
      icon: BookOpen,
      title: "How-To Guides – Step-by-Step Guides to Deliver",
      description: "Practical, real-world strategies you can apply immediately to master a new skill or refine your workflow."
    },
    {
      icon: BarChart2,
      title: "Polls & Surveys – Your Opinion Matters",
      description: "Weigh in on hot topics and compare your views with industry peers in our quick and insightful polls."
    },
    {
      icon: UserCheck,
      title: "Subscriber Spotlight – Your Achievements Deserve to Be Seen",
      description: "We showcase inspiring stories, big wins, and creative ideas from our very own talented readers."
    },
    {
      icon: Gavel,
      title: "Legal Updates and Policy Changes",
      description: "Stay up-to-date on the latest legislative changes and policy updates affecting the UK property market."
    }
  ];

  const cardColors = [
    { border: 'border-wl-blue', text: 'text-wl-blue' },
    { border: 'border-wl-green', text: 'text-wl-green' },
    { border: 'border-wl-navy', text: 'text-wl-navy' }
  ];

  return (
    <div className="min-h-screen pt-28">
      <SEO
        title="Whitefield Property Pulse - Newsletter"
        description="Subscribe to the Whitefield Property Pulse newsletter for property market insights, industry trends, legal updates, and expert analysis."
        keywords="property newsletter, conveyancing newsletter, property market insights, legal updates UK"
        canonicalUrl="/newsletter"
        structuredData={buildBreadcrumbs([{ name: "Home", path: "/" }, { name: "Newsletter" }])}
      />
      <Navbar />

      {/* Hero Section */}
      <section className="relative py-32 bg-wl-navy overflow-hidden">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="/property-pulse.mp4" type="video/mp4" />
          <div
            className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: 'url(/city-map.jpg)' }}
          ></div>
        </video>
        <div className="absolute inset-0 bg-wl-navy/70"></div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 md:p-8 border border-white/20">
            <img src="/whitefield-property-pulse.png" alt="Whitefield Property Pulse" className="w-80 mx-auto mb-6" />
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Stay Ahead of the Latest Property News
            </h1>

            {isSubmitted ? (
              <div className="max-w-lg mx-auto text-center">
                <div className="w-16 h-16 bg-wl-green/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-wl-green" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Successfully Subscribed!</h3>
                <p className="text-white/90 mb-4">
                  Welcome to Property Pulse! Check your email for a welcome message.
                </p>
                <p className="text-white/80 text-sm">
                  You'll receive your first newsletter soon with the latest property market insights.
                </p>
              </div>
            ) : (
              <Form {...form}>
                <form className="max-w-lg mx-auto" id="signup" onSubmit={form.handleSubmit(onSubmit)}>
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <div className="flex gap-3">
                          <FormControl>
                            <input
                              {...field}
                              type="email"
                              placeholder="Enter Your Email Address"
                              className={`flex-1 px-3 py-2 rounded-lg text-sm text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 ${
                                fieldState.error
                                  ? "ring-2 ring-red-500 focus:ring-red-500"
                                  : "focus:ring-wl-green"
                              }`}
                              disabled={isSubmitting}
                            />
                          </FormControl>
                          <button
                            type="submit"
                            disabled={isSubmitting}
                            className="bg-wl-green hover:bg-wl-green/90 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-300 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isSubmitting ? "Signing Up..." : "Sign Up!"}
                          </button>
                        </div>
                        <FormMessage className="text-red-300 mt-2 text-sm text-left" />
                      </FormItem>
                    )}
                  />
                  <p className="text-white/80 mt-3 text-sm font-semibold">Join Thousands of Active Readers</p>
                </form>
              </Form>
            )}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 scroll-animate">
            <h2 className="text-3xl md:text-4xl font-bold text-wl-navy mb-6">
              What You're Going to Discover...
            </h2>
            <p className="text-xl text-wl-gray max-w-3xl mx-auto">
              Each edition is packed with valuable insights, expert analysis, and actionable advice to keep you at the forefront of the property market.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const color = cardColors[index % cardColors.length];
              return (
                <div key={index} className={`bg-gray-50 p-8 rounded-xl shadow-sm border-l-4 ${color.border} scroll-animate transition-all hover:shadow-lg hover:-translate-y-1`}>
                  <feature.icon className={`w-10 h-10 ${color.text} mb-4`} />
                  <h3 className="font-bold text-wl-navy text-xl mb-3">{feature.title}</h3>
                  <p className="text-wl-gray">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-wl-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center scroll-animate">
          <h2 className="text-3xl font-bold text-white mb-4">
            Don't Miss Out.
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Join thousands of property professionals who rely on the Pulse to stay competitive and informed.
          </p>
          <Link
            to="/newsletter#signup"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('signup')?.scrollIntoView({ behavior: 'smooth' });
              const input = document.querySelector('input[type="email"]') as HTMLInputElement;
              input?.focus();
            }}
            className="bg-wl-green hover:bg-wl-green/90 text-white px-8 py-3 rounded-lg font-bold text-lg transition-all duration-300 inline-flex items-center justify-center gap-2"
          >
            Subscribe Now
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
